import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  const navigation = [
    { name: 'Services', href: '#services' },
    { name: 'About', href: '/about' },
    { name: 'Success Stories', href: '#client-highlights' },
    { name: 'Contact', href: '#contact' }
  ];

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const handleNavClick = (item: typeof navigation[0]) => {
    if (item.href.startsWith('#')) {
      if (isHomePage) {
        scrollToSection(item.href.replace('#', ''));
      } else {
        navigate('/');
        setTimeout(() => {
          const section = document.getElementById(item.href.replace('#', ''));
          if (section) {
            section.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    }
    setIsMenuOpen(false);
  };

  const openCalendly = () => {
    window.open('https://calendly.com/chanelle-proeditionssc/30min', '_blank');
  };

  return (
    <header className={`absolute top-0 left-0 right-0 z-50 py-6 ${!isHomePage ? 'bg-white shadow-sm' : ''}`} role="banner">
      <nav className="mx-auto px-6 lg:px-12" role="navigation" aria-label="Main navigation">
        <div className={`${isHomePage ? 'bg-white/10 backdrop-blur-md border border-white/20' : 'bg-white'} rounded-full px-8 py-4`}>
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <Link 
                to="/" 
                className={`text-xl font-medium ${isHomePage ? 'text-white' : 'text-gray-900'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1a4d2e] rounded-md px-2 py-1`}
                aria-label="ProEditions Home - Now Booking Projects for 2026"
              >
                ProEditions - Now Booking Projects for 2026
              </Link>
            </div>

            <div className="hidden md:flex items-center space-x-8">
              {navigation.map((item) => (
                item.href.startsWith('#') ? (
                  <button
                    key={item.name}
                    onClick={() => handleNavClick(item)}
                    className={`${isHomePage ? 'text-white/90 hover:text-white' : 'text-gray-700 hover:text-gray-900'} font-light transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1a4d2e] rounded-md px-2 py-1`}
                    aria-label={`Navigate to ${item.name} section`}
                  >
                    {item.name}
                  </button>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${isHomePage ? 'text-white/90 hover:text-white' : 'text-gray-700 hover:text-gray-900'} font-light transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1a4d2e] rounded-md px-2 py-1`}
                    aria-label={`Navigate to ${item.name} page`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
              
              <button
                onClick={openCalendly}
                className={`${isHomePage ? 'bg-white text-gray-900 hover:bg-gray-100' : 'bg-[#1a4d2e] text-white hover:bg-[#153d24]'} px-6 py-2.5 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1a4d2e]`}
                aria-label="Start a project with ProEditions"
              >
                Start a Project
              </button>
            </div>

            <div className="md:hidden">
              <button
                onClick={toggleMenu}
                className={`${isHomePage ? 'text-white hover:text-white/80' : 'text-gray-900 hover:text-gray-700'} cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1a4d2e] rounded-md p-2`}
                aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                aria-expanded={isMenuOpen}
                aria-controls="mobile-menu"
              >
                <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`} aria-hidden="true"></i>
              </button>
            </div>
          </div>

          {isMenuOpen && (
            <div 
              id="mobile-menu"
              className={`md:hidden ${isHomePage ? 'border-white/20' : 'border-gray-200'} border-t mt-4 pt-4`}
              role="menu"
            >
              <div className="space-y-4">
                {navigation.map((item) => (
                  item.href.startsWith('#') ? (
                    <button
                      key={item.name}
                      onClick={() => handleNavClick(item)}
                      className={`block ${isHomePage ? 'text-white/90 hover:text-white' : 'text-gray-700 hover:text-gray-900'} font-light cursor-pointer w-full text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1a4d2e] rounded-md px-2 py-1`}
                      role="menuitem"
                      aria-label={`Navigate to ${item.name} section`}
                    >
                      {item.name}
                    </button>
                  ) : (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsMenuOpen(false)}
                      className={`block ${isHomePage ? 'text-white/90 hover:text-white' : 'text-gray-700 hover:text-gray-900'} font-light cursor-pointer w-full text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1a4d2e] rounded-md px-2 py-1`}
                      role="menuitem"
                      aria-label={`Navigate to ${item.name} page`}
                    >
                      {item.name}
                    </Link>
                  )
                ))}
                
                <button
                  onClick={openCalendly}
                  className={`w-full ${isHomePage ? 'bg-white text-gray-900 hover:bg-gray-100' : 'bg-[#1a4d2e] text-white hover:bg-[#153d24]'} px-6 py-2.5 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap block text-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1a4d2e]`}
                  role="menuitem"
                  aria-label="Start a project with ProEditions"
                >
                  Start a Project
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
}
